/**
 * Client for the `view-as` Edge Function.
 *
 * Every operation is authorised server-side; nothing here is a permission
 * check. The UI hides the entry point from non-Super-Admins purely so the
 * button is not offered to people it would refuse.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import type { ViewAsRecord } from "@/lib/supabase/viewAs";

export interface ViewAsTarget {
  id: string;
  email: string | null;
  full_name: string | null;
  role_name: string | null;
}

export interface StartViewAsResult {
  impersonationId: string;
  /** One-time credential. Redeem immediately; never log it or put it in a URL. */
  tokenHash: string;
  expiresAt: string;
  target: ViewAsTarget;
}

/**
 * supabase-js collapses every non-2xx Edge Function response into
 * "Edge Function returned a non-2xx status code", which is useless to a person
 * looking at a toast. The real message is in the attached Response.
 */
async function readErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  const context = (error as { context?: unknown })?.context;
  if (context instanceof Response) {
    try {
      const payload = await context.clone().json();
      if (payload && typeof payload.error === "string") {
        return payload.error;
      }
    } catch {
      // Body was not JSON; fall through to the generic message.
    }
  }
  return error instanceof Error ? error.message : fallback;
}

async function invokeViewAs<T>(
  client: SupabaseClient,
  body: Record<string, unknown>,
  fallbackMessage: string,
): Promise<T> {
  const { data, error } = await client.functions.invoke("view-as", { body });

  if (error) {
    throw new Error(await readErrorMessage(error, fallbackMessage));
  }
  if (!data || (data as { success?: boolean }).success !== true) {
    throw new Error(
      ((data as { error?: string })?.error) || fallbackMessage,
    );
  }
  return data as T;
}

/**
 * Opens a view-as session. Called with the Super Admin's own session.
 *
 * Returns a one-time token that the caller must redeem on a separate,
 * session-storage-backed client — never on the admin's own client, which would
 * replace their session with the target's.
 */
export async function startViewAs(params: {
  targetUserId: string;
  reason?: string;
  client?: SupabaseClient;
}): Promise<StartViewAsResult> {
  const data = await invokeViewAs<{
    impersonation_id: string;
    token_hash: string;
    expires_at: string;
    target: ViewAsTarget;
  }>(
    params.client ?? supabase,
    {
      operation: "start",
      target_user_id: params.targetUserId,
      reason: params.reason,
    },
    "Could not start the view-as session",
  );

  return {
    impersonationId: data.impersonation_id,
    tokenHash: data.token_hash,
    expiresAt: data.expires_at,
    target: data.target,
  };
}

/**
 * Records which Supabase session belongs to this view-as row.
 *
 * Must be called with the newly minted target session, because the server reads
 * the session id from the caller's own JWT rather than trusting the body.
 */
export async function bindViewAs(params: {
  impersonationId: string;
  client: SupabaseClient;
}): Promise<void> {
  await invokeViewAs(
    params.client,
    { operation: "bind", impersonation_id: params.impersonationId },
    "Could not bind the view-as session",
  );
}

/** Unblocks writes for the rest of the session. Recorded in the ledger. */
export async function enableViewAsActions(
  client: SupabaseClient = supabase,
): Promise<void> {
  await invokeViewAs(
    client,
    { operation: "set_actions" },
    "Could not enable actions",
  );
}

/**
 * Ends a view-as session.
 *
 * From inside the impersonated tab, pass no id: the server identifies the
 * session from the caller's JWT. From the Users page, pass the id to end
 * somebody's forgotten session remotely.
 */
export async function endViewAs(params?: {
  impersonationId?: string;
  client?: SupabaseClient;
}): Promise<void> {
  await invokeViewAs(
    params?.client ?? supabase,
    {
      operation: "end",
      ...(params?.impersonationId
        ? { impersonation_id: params.impersonationId }
        : {}),
    },
    "Could not end the view-as session",
  );
}

/** One row of the view-as ledger, as the Users page renders it. */
export interface ImpersonationLogEntry {
  id: string;
  admin_user_id: string;
  target_user_id: string;
  reason: string | null;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  end_reason: string | null;
  actions_enabled: boolean;
  admin_name?: string | null;
}

/**
 * History of view-as sessions opened against one user.
 *
 * RLS restricts the table to Super Admins, so this returns an empty list rather
 * than an error for anyone else.
 */
export async function listViewAsHistory(
  targetUserId: string,
  limit = 20,
): Promise<ImpersonationLogEntry[]> {
  const { data, error } = await supabase
    .from("es_impersonation_sessions")
    .select("*")
    .eq("target_user_id", targetUserId)
    .order("started_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("[view-as] could not load history:", error.message);
    return [];
  }

  const rows = (data ?? []) as ImpersonationLogEntry[];
  if (rows.length === 0) return rows;

  // Resolve the admin names in one extra query. A join from
  // es_impersonation_sessions to es_users has two candidate foreign keys, and
  // an unnamed embed raises PGRST201 — the same trap that silently broke the
  // auth health check's admin lookup.
  const adminIds = Array.from(new Set(rows.map((r) => r.admin_user_id)));
  const { data: admins } = await supabase
    .from("es_staff_directory")
    .select("id, full_name")
    .in("id", adminIds);

  const nameById = new Map(
    (admins ?? []).map((a: { id: string; full_name: string | null }) => [
      a.id,
      a.full_name,
    ]),
  );

  return rows.map((row) => ({
    ...row,
    admin_name: nameById.get(row.admin_user_id) ?? null,
  }));
}

/** Convenience for the banner: seconds left, never negative. */
export function secondsRemaining(record: ViewAsRecord): number {
  return Math.max(
    0,
    Math.floor((Date.parse(record.expiresAt) - Date.now()) / 1000),
  );
}
